'use client'

import { usePathname } from 'next/navigation'
import { ChevronRight, ChevronLeft, Eye, Package, Phone, Receipt, Shield, X } from 'lucide-react'
import { resolveColorHex, getVendorPrimaryColor } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { useOrderLookup } from '@/lib/commerce/hooks'

const STEPS = [
  { num: '01', title: 'Захиалгын дугаар', body: 'Имэйл болон SMS-ээр ирсэн баталгаажуулалтын захиалгын дугаарыг олно уу. Жишээ : DS25111222.', icon: Receipt },
  { num: '02', title: 'Бүртгэлтэй утас', body: 'Захиалгаа үүсгэхдээ оруулсан 8 оронтой утасны дугаараа бичнэ.', icon: Phone },
  { num: '03', title: 'Захиалгаа харах', body: 'Хүргэлтийн явц, төлбөрийн мэдээлэл зэрэг бүх мэдээллээ авах боломжтой.', icon: Eye },
] as const

export default function OrderCheck({ vendor, notFound, initialOrderNum, initialPhoneNum }: { vendor?: any, notFound?: boolean, initialOrderNum?: string, initialPhoneNum?: string }) {
  const pathname = usePathname()
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))
  const { orderNum, setOrderNum, phoneNum, setPhoneNum, errors, isLoading, submit } = useOrderLookup({ initialOrderNum, initialPhoneNum })
  const valid = orderNum.trim().length >= 4 && phoneNum.replace(/\D/g, '').length >= 8

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); submit() }
  }

  return (
    <div className="bg-cream font-inter">
      <section className="relative overflow-hidden border-b border-foreground/5 bg-cream">
        <BubbleField color={primary} />
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 lg:gap-16 items-center">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-white border border-foreground/5 text-foreground/60">
                <Package className="size-3" />Захиалга шалгах
              </div>
              <h1 className="mt-5 text-5xl sm:text-6xl leading-[1.02] font-bold font-gilroy tracking-[-0.025em] text-foreground">
                Захиалга <span style={{ background: `linear-gradient(to right, ${primary}, color-mix(in oklch, ${primary} 45%, white))`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>хаана</span> явж буйг хэдхэн секундэд харах.
              </h1>
              <p className="mt-5 text-[15px] text-foreground/65 leading-7">
                Захиалгын дугаар болон утасны дугаараар нэвтрэхгүйгээр шалгах боломжтой. Бүртгэлтэй бол{' '}
                <a href={navigateUrl('/profile', pathname)} className="text-foreground font-semibold underline-offset-4 hover:underline">Профайл</a>
                {' '}-аас бүх захиалгаа харна уу.
              </p>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); submit() }}
              className="w-full lg:w-105 rounded-3xl border border-foreground/5 bg-white shadow-[0_24px_48px_-24px_rgba(15,8,40,0.18)] p-6 sm:p-7 relative"
            >
              <h2 className="text-[18px] font-bold font-gilroy tracking-[-0.01em]">Захиалгаа шалгах</h2>
              <p className="mt-1 text-[12px] text-foreground/55">Мэдээллийг зөвхөн баталгаажуулахад ашиглана.</p>

              <div className="mt-5 space-y-3.5">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/55">Захиалгын дугаар</label>
                  <div className="mt-1.5 relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground/45 text-sm font-medium select-none">#</span>
                    <input
                      value={orderNum}
                      onChange={(e) => setOrderNum(e.target.value)}
                      onKeyDown={onKey}
                      placeholder="DS25111222"
                      className="w-full h-12 pl-8 pr-3 rounded-xl border border-foreground/10 bg-cream text-sm font-medium tabular-nums tracking-wider uppercase placeholder:text-foreground/35 placeholder:font-normal focus:outline-none focus:bg-white transition-colors"
                      style={{ ['--tw-ring-color' as any]: primary }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${primary}33` }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.boxShadow = '' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/55">Захиалгад ашигласан утас</label>
                  <div className="mt-1.5 relative flex items-stretch">
                    <span className="inline-flex items-center px-3 rounded-l-xl border border-r-0 border-foreground/10 bg-foreground/5 text-sm text-foreground/60 tabular-nums font-medium select-none">+976</span>
                    <input
                      value={phoneNum}
                      onChange={(e) => setPhoneNum(e.target.value.replace(/\D/g, '').slice(0, 8))}
                      onKeyDown={onKey}
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      placeholder="9911 2233"
                      className="flex-1 h-12 px-3 rounded-r-xl border border-foreground/10 bg-cream text-sm font-medium tabular-nums tracking-wider placeholder:text-foreground/35 placeholder:font-normal focus:outline-none focus:bg-white transition-colors"
                      onFocus={(e) => { e.currentTarget.style.borderColor = primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${primary}33` }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.boxShadow = '' }}
                    />
                  </div>
                </div>
              </div>

              {(errors.orderNum || errors.phoneNum) && (
                <div className="mt-3 text-[12px] text-red-600 font-medium">
                  {errors.orderNum || errors.phoneNum}
                </div>
              )}

              <button
                type="submit"
                disabled={!valid || isLoading}
                className="mt-5 w-full h-12 rounded-full text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-95 transition-opacity"
                style={{ background: primary }}
              >
                {isLoading ? (
                  <><span className="w-3.5 h-3.5 rounded-full border-2 border-white border-r-transparent animate-spin" />Шалгаж байна…</>
                ) : (
                  <>Захиалга харах<ChevronRight className="size-3.5" /></>
                )}
              </button>

              <div className="mt-4 pt-4 border-t border-foreground/5 text-[11px] text-foreground/55 flex items-center gap-1.5">
                <Shield className="size-3" />Таны утсыг зөвхөн энэ захиалгатай тааруулахад ашиглана.
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 py-10">
        {notFound && <NotFoundCard primary={primary} pathname={pathname} />}
      </section>

      <section className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pb-16">
        <div className="flex items-end justify-between mb-6 gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-foreground/55">
              <span className="h-px w-6 bg-current opacity-40" />Хэрхэн ашиглах
            </div>
            <h2 className="mt-2 text-3xl lg:text-[40px] font-bold font-gilroy tracking-[-0.02em] text-foreground">
              3 алхмаар захиалгаа харна.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {STEPS.map((s) => {
            const Icon = s.icon
            return (
              <div key={s.num} className="relative rounded-3xl bg-white border border-foreground/5 p-6 hover:-translate-y-0.5 transition-transform">
                <div className="flex items-start justify-between">
                  <div className="h-11 w-11 rounded-2xl grid place-items-center" style={{ background: `color-mix(in oklch, ${primary} 12%, white)`, color: primary }}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="text-[40px] font-bold font-gilroy tabular-nums leading-none tracking-[-0.025em]" style={{ color: `color-mix(in oklch, ${primary} 22%, white)` }}>
                    {s.num}
                  </div>
                </div>
                <div className="mt-5 text-[17px] font-bold font-gilroy tracking-[-0.01em] text-foreground">{s.title}</div>
                <p className="mt-1.5 text-[13.5px] text-foreground/60 leading-relaxed">{s.body}</p>
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}

function NotFoundCard({ primary, pathname }: { primary: string, pathname: string }) {
  return (
    <div className="rounded-3xl border border-foreground/5 bg-white shadow-[0_24px_48px_-28px_rgba(15,8,40,0.12)] p-6 sm:p-8">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-2xl bg-rose-50 border border-rose-200/60 inline-flex items-center justify-center text-rose-600 shrink-0">
          <X className="size-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[18px] font-bold font-gilroy tracking-[-0.01em]">Захиалга олдсонгүй</h3>
          <p className="mt-1 text-[13.5px] text-foreground/65 leading-6">
            Захиалгын дугаар, эсвэл утасны дугаар буруу байна. Та захиалгын мэдээллээ дахин нэг шалгана уу.
          </p>
          <ul className="mt-4 space-y-2 text-[13px]">
            <Tip>Имэйл эсвэл SMS-ээр ирсэн баталгаажуулалт дахь дугаарыг зөв хуулж оруулна уу.</Tip>
            <Tip>Утасны дугаар нь захиалга өгсөн үеийн дугаар байх ёстой.</Tip>
          </ul>
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' }) }}
              className="h-10 px-4 rounded-full text-[13px] font-semibold text-white inline-flex items-center gap-1.5 hover:opacity-95 transition-opacity"
              style={{ background: primary }}
            >
              <ChevronLeft className="size-3.5" />Дахин оролдох
            </button>
            <a
              href={navigateUrl('/contact', pathname)}
              className="h-10 px-4 rounded-full text-[13px] font-semibold text-foreground inline-flex items-center gap-1.5 border border-foreground/15 hover:bg-foreground/5 transition-colors"
            >
              <Phone className="size-3.5" />Холбогдох мэдээлэл
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-foreground/65">
      <span className="mt-2 w-1 h-1 rounded-full bg-foreground/30 shrink-0" />
      <span>{children}</span>
    </li>
  )
}

function BubbleField({ color }: { color: string }) {
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1280 720" aria-hidden="true">
      <defs>
        <linearGradient id="oc-bubble" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0.04" />
        </linearGradient>
      </defs>
      <circle cx="120" cy="80" r="180" fill="url(#oc-bubble)" />
      <circle cx="1180" cy="220" r="220" fill="url(#oc-bubble)" opacity="0.85" />
      <circle cx="380" cy="640" r="160" fill="url(#oc-bubble)" opacity="0.75" />
      <circle cx="820" cy="600" r="120" fill="url(#oc-bubble)" opacity="0.65" />
    </svg>
  )
}
